import { useMemo } from 'react';

import { enhancedApi as providerDirectoryService } from '@/services/api/generated/providersService';
import type {
  DirectoryHandle,
  DirectoryProvider,
  DirectoryProviderUnit,
} from '@/services/types/provider-directory-types';

interface UseProviderDirectoryOptions {
  providerRefId?: string | null;
  skip?: boolean;
}

interface UseProviderDirectoryResult {
  provider?: DirectoryProvider;
  units: DirectoryProviderUnit[];
  handlesByUnit: Record<string, DirectoryHandle[]>;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Centralized hook for fetching and normalizing provider directory data.
 * Uses the generated RTK query to fetch provider with embedded units and handles.
 */
export const useProviderDirectory = (
  options: UseProviderDirectoryOptions,
): UseProviderDirectoryResult => {
  const { providerRefId, skip } = options;
  const effectiveSkip = skip || !providerRefId;

  const query = providerDirectoryService.useProvidersRetrieveQuery(
    { id: providerRefId as string },
    { skip: effectiveSkip },
  );

  const normalized = useMemo<{
    provider?: DirectoryProvider;
    units: DirectoryProviderUnit[];
    handlesByUnit: Record<string, DirectoryHandle[]>;
  }>(() => {
    if (!query.data) {
      return { provider: undefined, units: [], handlesByUnit: {} };
    }

    const provider = query.data;
    const units = provider.units ?? [];

    const handlesByUnit = units.reduce<Record<string, DirectoryHandle[]>>(
      (acc, unit) => {
        acc[String(unit.id)] = unit.handles ?? [];
        return acc;
      },
      {},
    );

    return {
      provider,
      units,
      handlesByUnit,
    };
  }, [query.data]);

  return {
    provider: normalized.provider,
    units: normalized.units,
    handlesByUnit: normalized.handlesByUnit,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

// Export mutations and queries from generated service
export const {
  useProvidersListQuery,
  useProvidersRetrieveQuery,
  useProvidersCreateMutation,
  useProvidersUpdateMutation,
  useProvidersUnitsListQuery,
  useProvidersUnitsCreateMutation,
  useProvidersUpdateUnitMutation,
  useProvidersAddUnitHandleMutation,
  useProvidersUpdateUnitHandleMutation,
  useProvidersSetPrimaryHandleMutation,
  useProvidersContactsListQuery,
  useProvidersContactsSearchQuery,
  useProvidersSuggestedContactQuery,
  useProvidersSearchQuery,
} = providerDirectoryService;

export type {
  ContactHandleSchema,
  ContactRequestCapabilities,
  ProviderContactUnitType,
} from '@/services/api/generated/providersService';
export type {
  DirectoryHandle,
  DirectoryProvider,
  DirectoryProviderUnit,
  DirectoryProvider as ProviderDirectory,
} from '@/services/types/provider-directory-types';
